'use client';

import { useEffect, useRef, useState, createElement } from 'react';
import {
  ANIMATION_TITLES_INCREMENT,
  ANIMATION_TEXT_INCREMENT,
  ANIMATION_GLOBAL_OFFSET,
} from '@/lib/animationConfig';

export default function AnimatedText({
  children,
  variant = 'fade',
  baseDelay = 0,
  globalOffset = ANIMATION_GLOBAL_OFFSET,
  className = '',
}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const checkVisibility = () => {
      if (!document.body.classList.contains('page-enter') && ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        setInView(isVisible);
      }
    };

    const margin =
      window.innerWidth < 768
        ? '0px 0px -50px 0px'
        : '0px 0px -80px 0px';

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!document.body.classList.contains('page-enter')) {
          setInView(entry.isIntersecting);
        }
      },
      { threshold: 0, rootMargin: margin }
    );

    const mutation = new MutationObserver(() => {
      if (!document.body.classList.contains('page-enter')) {
        checkVisibility();
      }
    });

    if (ref.current) observer.observe(ref.current);
    mutation.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      mutation.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!inView || !ref.current) return;
    const words = ref.current.querySelectorAll('.word');
    const isTitle = ref.current.querySelector('h1,h2,h3,h4,h5,h6');
    const increment = isTitle ? ANIMATION_TITLES_INCREMENT : ANIMATION_TEXT_INCREMENT;

    words.forEach((el, i) => {
      const delay = baseDelay + globalOffset + i * increment;
      el.style.animationDelay = `${delay}s`;

      const link = el.closest('a');
      if (link && !link.dataset.animated) {
        link.style.setProperty('--link-delay', `${delay - increment}s`);
        link.dataset.animated = 'true';
      }
    });
  }, [inView, baseDelay, globalOffset]);

  const wrapWords = (node, keyPrefix = '') => {
    if (typeof node === 'string') {
      const parts = node.match(/(\S+\s+[?!:])|(\S+)|(\s+)/g) || [];
      return parts.map((part, i) =>
        part.trim() === ''
          ? <span key={`${keyPrefix}-space-${i}`}>{part}</span>
          : <span key={`${keyPrefix}-word-${i}`} className={`word ${variant}`}>{part}</span>
      );
    }
    if (Array.isArray(node)) {
      return node.flatMap((child, i) => wrapWords(child, `${keyPrefix}-${i}`));
    }
    if (typeof node === 'object' && node !== null && node.type && node.props) {
      return createElement(
        node.type,
        { ...node.props, key: keyPrefix },
        wrapWords(node.props.children, `${keyPrefix}-child`)
      );
    }
    if (typeof node === 'object' && node?.props?.children) {
      return wrapWords(node.props.children, `${keyPrefix}-child`);
    }
    return null;
  };

  return (
    <div ref={ref} className={`animated-text ${inView ? 'inview' : ''} ${className}`}>
      {wrapWords(children, 'root')}
    </div>
  );
}